import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Check, Clock, Dumbbell, Target, TrendingUp, MessageCircle, X, Star, Link2
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { onMaxUpdate, emitMaxUpdate } from "./syncEvents";

const GuidedWorkoutMode = ({ workout, userMaxes: initialUserMaxes, equipmentList, onComplete, onClose, programContext = null, preWorkoutMood = null, isDeloadMode: initialDeload = false }) => {
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentSetIndex, setCurrentSetIndex] = useState(0);
  const [startTime] = useState(Date.now());
  const [isResting, setIsResting] = useState(false);
  const [restTimeLeft, setRestTimeLeft] = useState(0);
  const [setLogs, setSetLogs] = useState({}); // { "exerciseIndex-setIndex": { weight, reps, completed, feedback } }
  const [actualWeight, setActualWeight] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [workoutComplete, setWorkoutComplete] = useState(false);
  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const chatEndRef = useRef(null);
  
  // NEW: Conversational states
  const [conversationStep, setConversationStep] = useState("ask_max"); // "ask_max", "show_exercise", "ask_heavier"
  const [userMaxes, setUserMaxes] = useState(initialUserMaxes || {});
  const [tempMaxInput, setTempMaxInput] = useState("");
  const [heavierResponse, setHeavierResponse] = useState(null);

  // Keep local maxes in sync with parent (Classic mode may have updated them)
  useEffect(() => {
    if (initialUserMaxes) {
      setUserMaxes(prev => ({ ...prev, ...initialUserMaxes }));
    }
  }, [initialUserMaxes]);

  // Flatten exercises from sections
  const allExercises = useMemo(() => {
    if (!workout.sections) return [];
    return workout.sections.flatMap((section, sectionIdx) => 
      (section.exercises || []).map((ex, exIdx) => ({
        ...ex,
        sectionIndex: sectionIdx,
        sectionName: section.name || `Block ${String.fromCharCode(65 + sectionIdx)}`,
        originalExerciseIndex: exIdx
      }))
    );
  }, [workout]);

  // On mount: skip asking for max if we already have it for the first exercise
  useEffect(() => {
    if (allExercises.length > 0 && initialUserMaxes) {
      const firstEx = allExercises[0];
      if (firstEx && initialUserMaxes[firstEx.exercise_id]) {
        setConversationStep("show_exercise");
      }
    }
    // Listen for max updates from other components
    const unsub = onMaxUpdate((exerciseId, maxWeight) => {
      setUserMaxes(prev => ({ ...prev, [exerciseId]: maxWeight }));
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save max to backend (shared with Classic mode via /api/workouts/maxes)
  const persistMax = async (exerciseId, exerciseName, maxWeight) => {
    try {
      await fetch(`${API}/workouts/maxes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ exercise_id: exerciseId, exercise_name: exerciseName, max_weight: maxWeight })
      });
      emitMaxUpdate(exerciseId, maxWeight);
    } catch (error) {
      console.error("Failed to persist max:", error);
    }
  };

  const currentExercise = allExercises[currentExerciseIndex];
  const totalExercises = allExercises.length;
  
  // Get number of sets for current exercise
  const getNumSets = (exercise) => exercise?.sets || exercise?.sets_data?.length || 3;
  const currentNumSets = currentExercise ? getNumSets(currentExercise) : 0;
  
  // Get target reps for current set
  const getTargetReps = (exercise, setIndex) => {
    const setsData = exercise?.sets_data || [];
    if (setsData[setIndex]?.reps) return setsData[setIndex].reps;
    return exercise?.reps || "5";
  };

  // Get rest time for current set (in seconds)
  const getRestTime = (exercise, setIndex) => {
    const setsData = exercise?.sets_data || [];
    if (setsData[setIndex]?.rest_mins) return parseInt(setsData[setIndex].rest_mins) * 60;
    return exercise?.rest_seconds || 120;
  };

  // Get percentage for current set
  const getPercentage = (exercise, setIndex) => {
    const setsData = exercise?.sets_data || [];
    if (setsData[setIndex]?.percent_max) {
      return parseFloat(setsData[setIndex].percent_max.replace("%", ""));
    }
    return null;
  };

  // Calculate target weight based on max and percentage
  const calculateTargetWeight = (exercise, setIndex) => {
    // Use based_off exercise max if set, otherwise use own max
    const basedOffId = exercise?.based_off_exercise_id;
    const maxWeight = (basedOffId && basedOffId !== exercise?.exercise_id)
      ? userMaxes[basedOffId]
      : userMaxes[exercise?.exercise_id];
    const percentage = getPercentage(exercise, setIndex);
    if (!maxWeight || !percentage) return null;
    return Math.round((maxWeight * (percentage / 100)) / 5) * 5;
  };

  // Find matching equipment - prefer direct bar_equipment_id link, fall back to name matching
  const getMatchedEquipment = (exercise) => {
    if (exercise?.bar_equipment_id) {
      const directMatch = equipmentList.find(eq => eq.equipment_id === exercise.bar_equipment_id);
      if (directMatch) return directMatch;
    }
    const exerciseName = (exercise?.exercise_name || "").toLowerCase();
    const sorted = [...equipmentList].sort((a, b) => (b.name || "").length - (a.name || "").length);
    for (const equip of sorted) {
      if (equip.name && exerciseName.includes(equip.name.toLowerCase())) {
        return equip;
      }
    }
    return null;
  };

  // Calculate plate breakdown
  const AVAILABLE_PLATES = [45, 35, 25, 10, 5, 2.5];
  const calculatePlateBreakdown = (totalWeight, barWeight) => {
    if (!barWeight || !totalWeight || totalWeight <= barWeight) return null;
    
    let platesPerSide = (totalWeight - barWeight) / 2;
    platesPerSide = Math.round(platesPerSide / 2.5) * 2.5;
    
    const plates = [];
    let remaining = platesPerSide;
    
    for (const plateWeight of AVAILABLE_PLATES) {
      if (remaining >= plateWeight) {
        const count = Math.floor(remaining / plateWeight);
        if (count > 0) {
          plates.push({ weight: plateWeight, count });
          remaining -= count * plateWeight;
        }
      }
    }
    
    return plates;
  };

  const formatPlateBreakdown = (plates) => {
    if (!plates || plates.length === 0) return "Bar only";
    return plates.map(p => `${p.weight}${p.weight % 1 === 0 ? '' : ''}×${p.count}`).join(" + ");
  };

  // Rest timer effect
  useEffect(() => {
    if (!isResting || restTimeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setRestTimeLeft(prev => {
        if (prev <= 1) {
          setIsResting(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isResting, restTimeLeft]);

  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentExerciseIndex, currentSetIndex, isResting, showFeedback, workoutComplete]);

  // Handle set completion
  const handleSetComplete = (feedback) => {
    const logKey = `${currentExerciseIndex}-${currentSetIndex}`;
    const targetWeight = calculateTargetWeight(currentExercise, currentSetIndex);
    
    setSetLogs(prev => ({
      ...prev,
      [logKey]: {
        weight: actualWeight || targetWeight || 0,
        reps: getTargetReps(currentExercise, currentSetIndex),
        completed: true,
        feedback
      }
    }));
    
    setShowFeedback(false);
    setActualWeight("");
    
    // Check if more sets in current exercise
    if (currentSetIndex < currentNumSets - 1) {
      // Start rest timer, then move to next set
      const restTime = getRestTime(currentExercise, currentSetIndex);
      setRestTimeLeft(restTime);
      setIsResting(true);
      setCurrentSetIndex(prev => prev + 1);
    } else {
      // All sets done for this exercise.
      // Only Strength exercises get the heavier-prompt — Speed/Power/Stamina
      // etc. don't fit the "add weight next time" UX, so we just advance.
      const isStrength =
        (currentExercise?.category || "").toLowerCase() === "strength";
      if (isStrength) {
        setConversationStep("ask_heavier");
      } else {
        advanceToNextExerciseOrFinish();
      }
    }
  };

  // Move to next exercise (or finish workout) without firing the heavier prompt.
  const advanceToNextExerciseOrFinish = () => {
    if (currentExerciseIndex < totalExercises - 1) {
      const nextExIdx = currentExerciseIndex + 1;
      const nextEx = allExercises[nextExIdx];
      setCurrentExerciseIndex(nextExIdx);
      setCurrentSetIndex(0);
      setHeavierResponse(null);
      if (nextEx && userMaxes[nextEx.exercise_id]) {
        setConversationStep("show_exercise");
      } else {
        setConversationStep("ask_max");
      }
    } else {
      setWorkoutComplete(true);
    }
  };

  // Handle "go heavier" response — receives the explicit lbs delta the athlete
  // chose from the scroll picker (e.g. -2.5, 0, +5, +7.5). 0 = no-op.
  const handleHeavierResponse = async (delta) => {
    setHeavierResponse(delta);

    const setsForThisExercise = Object.entries(setLogs)
      .filter(([k]) => k.startsWith(`${currentExerciseIndex}-`))
      .map(([, v]) => Number(v?.weight) || 0);
    const lastWeight = setsForThisExercise.length ? Math.max(...setsForThisExercise) : 0;

    if (currentExercise?.exercise_id && lastWeight > 0) {
      try {
        const res = await fetch(`${API}/progression/heavier-feedback`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            exercise_id: currentExercise.exercise_id,
            exercise_name: currentExercise.exercise_name || "",
            last_weight: lastWeight,
            delta_override: Number(delta),
            response: delta > 0 ? "yes" : delta < 0 ? "lighter" : "same",
            workout_id: workout?.workout_id || "",
            slot_name: currentExercise.slot_name || "",
          }),
        });
        if (res.ok) {
          const d = await res.json();
          if (d.applied) {
            setUserMaxes((prev) => ({
              ...prev,
              [currentExercise.exercise_id]: d.new_max,
            }));
            emitMaxUpdate(currentExercise.exercise_id, d.new_max);
            const arrow = d.new_max > d.previous_max ? "↑" : "↓";
            toast.success(`Max ${arrow} ${d.previous_max} → ${d.new_max} lbs`);
          }
        }
      } catch (e) {
        console.error("heavier-feedback failed", e);
      }
    }

    advanceToNextExerciseOrFinish();
  };

  // Handle max weight submission
  const handleMaxSubmit = () => {
    if (tempMaxInput && currentExercise) {
      const exerciseId = currentExercise.exercise_id;
      if (!exerciseId) return;
      const maxVal = parseFloat(tempMaxInput);
      setUserMaxes(prev => ({
        ...prev,
        [exerciseId]: maxVal
      }));
      // Persist to backend so Classic mode and other sessions see it
      persistMax(exerciseId, currentExercise.exercise_name, maxVal);
    }
    setTempMaxInput("");
    setConversationStep("show_exercise");
  };

  // Skip max entry (use default or no percentage calculations)
  const skipMaxEntry = () => {
    setTempMaxInput("");
    setConversationStep("show_exercise");
  };

  // Skip rest timer
  const skipRest = () => {
    setIsResting(false);
    setRestTimeLeft(0);
  };

  // Save workout
  const handleSaveWorkout = async () => {
    setSaving(true);
    const durationMinutes = Math.round((Date.now() - startTime) / 60000);
    
    // Build exercise logs
    const exerciseLogArray = allExercises.map((exercise, exIdx) => {
      const sets = [];
      const numSets = getNumSets(exercise);
      for (let setIdx = 0; setIdx < numSets; setIdx++) {
        const log = setLogs[`${exIdx}-${setIdx}`];
        sets.push({
          weight: log?.weight ? parseFloat(log.weight) : null,
          reps: log?.reps ? parseInt(log.reps) : null,
          completed: log?.completed || false
        });
      }
      return {
        exercise_id: exercise.exercise_id,
        exercise_name: exercise.exercise_name,
        sets
      };
    });

    try {
      const response = await fetch(`${API}/workouts/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          workout_id: workout.workout_id,
          duration_minutes: durationMinutes || workout.estimated_duration || 45,
          rating: rating || null,
          notes: notes || null,
          program_id: programContext?.program_id || null,
          week_number: programContext?.week || null,
          day_number: programContext?.day || null,
          exercise_logs: exerciseLogArray,
          pre_workout_mood: preWorkoutMood,
          switched_to_deload: initialDeload,
        })
      });

      if (response.ok) {
        toast.success("Workout saved! Great job!");
        if (onComplete) onComplete();
        onClose();
      } else {
        toast.error("Failed to save workout");
      }
    } catch (error) {
      console.error("Error saving workout:", error);
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate elapsed time
  const getElapsedTime = () => {
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    return formatTime(elapsed);
  };

  // Current exercise data
  const targetWeight = currentExercise ? calculateTargetWeight(currentExercise, currentSetIndex) : null;
  const matchedEquipment = currentExercise ? getMatchedEquipment(currentExercise) : null;
  const plateBreakdown = targetWeight && matchedEquipment 
    ? calculatePlateBreakdown(targetWeight, matchedEquipment.weight) 
    : null;
  const targetReps = currentExercise ? getTargetReps(currentExercise, currentSetIndex) : 0;
  const percentage = currentExercise ? getPercentage(currentExercise, currentSetIndex) : null;
  const userMax = currentExercise ? userMaxes[currentExercise.exercise_id] : null;

  // Progress stats
  const completedSets = Object.keys(setLogs).length;
  const totalSets = allExercises.reduce((acc, ex) => acc + getNumSets(ex), 0);
  const progressPercent = totalSets > 0 ? Math.round((completedSets / totalSets) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/95 flex flex-col z-50"
      data-testid="guided-workout-mode"
    >
      {/* Header */}
      <div className="flex-shrink-0 bg-surface border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={onClose}
            className="p-2 hover:bg-[#1E293B] rounded-lg"
            data-testid="close-guided-mode"
          >
            <X className="w-5 h-5" />
          </button>
          <div>
            <h2 className="font-heading text-lg font-bold break-words">{workout.title}</h2>
            <p className="text-xs text-text-secondary">{getElapsedTime()} elapsed</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs text-text-secondary">Progress</p>
          <p className="text-sm font-bold text-primary">{progressPercent}%</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="flex-shrink-0 h-1 bg-[#1E293B]">
        <div 
          className="h-full bg-primary transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Welcome Message */}
        <div className="flex gap-3">
          <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Dumbbell className="w-4 h-4 text-primary" />
          </div>
          <div className="bg-[#1E293B] rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
            <p className="text-sm">Let's crush this workout! I'll guide you through each set.</p>
          </div>
        </div>

        {/* Workout Complete State */}
        {workoutComplete ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-success/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-success" />
              </div>
              <div className="bg-success/20 border border-success/30 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
                <p className="text-sm font-medium text-success">Workout Complete!</p>
                <p className="text-sm text-text-secondary mt-1">
                  You crushed {completedSets} sets across {totalExercises} exercises!
                </p>
              </div>
            </div>

            {/* Rating */}
            <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
              <p className="text-sm text-text-secondary">How was that workout?</p>
              <div className="flex gap-2 justify-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    className={`p-2 rounded-lg transition-colors ${
                      rating >= star ? "text-amber-400" : "text-[#475569]"
                    }`}
                  >
                    <Star className={`w-8 h-8 ${rating >= star ? "fill-current" : ""}`} />
                  </button>
                ))}
              </div>
              
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any notes about this workout?"
                className="w-full bg-[#1E293B] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-white resize-none"
                rows={2}
              />
              
              <button
                onClick={handleSaveWorkout}
                disabled={saving}
                className="w-full bg-success text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Save Workout
                  </>
                )}
              </button>
            </div>
          </motion.div>
        ) : isResting ? (
          /* Rest Timer State */
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex gap-3"
          >
            <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 text-amber-400" />
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl rounded-tl-sm px-4 py-4 flex-1">
              <p className="text-sm text-amber-400 font-medium mb-2">Rest Time</p>
              <p className="font-heading text-4xl font-bold text-white tabular-nums">
                {formatTime(restTimeLeft)}
              </p>
              {/* Next set preview with weight and plates */}
              {(() => {
                const nextWeight = calculateTargetWeight(currentExercise, currentSetIndex);
                const nextReps = getTargetReps(currentExercise, currentSetIndex);
                const nextPlates = nextWeight && matchedEquipment
                  ? calculatePlateBreakdown(nextWeight, matchedEquipment.weight)
                  : null;
                return (
                  <div className="mt-3 pt-3 border-t border-amber-500/20">
                    <p className="text-xs text-white/80 italic mb-2">Load your next weight first, then use the remaining time to rest.</p>
                    <p className="text-xs text-text-secondary mb-1">
                      Up next: Set {currentSetIndex + 1} of {currentNumSets}
                    </p>
                    {nextWeight ? (
                      <p className="text-sm font-semibold text-white">{nextWeight} lbs × {nextReps} reps</p>
                    ) : (
                      <p className="text-sm text-white">{nextReps} reps</p>
                    )}
                    {nextPlates && nextPlates.length > 0 && (
                      <>
                        <p className="text-xs text-accent mt-1">Each side: {formatPlateBreakdown(nextPlates)}</p>
                        <p className="text-[10px] text-amber-400/70 italic mt-1">Inner plates go on first, come off last</p>
                      </>
                    )}
                  </div>
                );
              })()}
              <button
                onClick={skipRest}
                className="mt-3 text-sm text-amber-400 hover:text-amber-300"
              >
                Skip Rest →
              </button>
            </div>
          </motion.div>
        ) : conversationStep === "ask_heavier" ? (
          /* Ask if they can go heavier - after completing all sets */
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-success/20 rounded-full flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-success" />
              </div>
              <div className="bg-[#1E293B] rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
                <p className="text-sm font-medium text-success mb-1">Great work on {currentExercise?.exercise_name}!</p>
                <p className="text-sm">Do you think you could go heavier next time?</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 ml-11">
              {[
                { label: "Yes, definitely!", value: "yes", color: "bg-green-500/20 text-green-400 border-green-500/30" },
                { label: "Maybe a little", value: "maybe", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
                { label: "Same weight is good", value: "same", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
                { label: "Need to go lighter", value: "lighter", color: "bg-red-500/20 text-red-400 border-red-500/30" },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleHeavierResponse(option.value)}
                  className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors hover:scale-105 ${option.color}`}
                  data-testid={`heavier-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </motion.div>
        ) : showFeedback ? (
          /* Feedback State */
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-4 h-4 text-primary" />
              </div>
              <div className="bg-[#1E293B] rounded-2xl rounded-tl-sm px-4 py-3">
                <p className="text-sm">How did that set feel?</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 ml-11">
              {[
                { label: "Easy", value: "easy", color: "bg-green-500/20 text-green-400 border-green-500/30" },
                { label: "Good", value: "good", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
                { label: "Hard", value: "hard", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
                { label: "Failed", value: "failed", color: "bg-red-500/20 text-red-400 border-red-500/30" },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSetComplete(option.value)}
                  className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors hover:scale-105 ${option.color}`}
                  data-testid={`feedback-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </motion.div>
        ) : currentExercise ? (
          /* Current Set State */
          <motion.div 
            key={`${currentExerciseIndex}-${currentSetIndex}-${conversationStep}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Ask for Max - before showing exercise */}
            {conversationStep === "ask_max" && currentSetIndex === 0 ? (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-primary">
                      {String.fromCharCode(65 + (currentExercise.sectionIndex || 0))}
                    </span>
                  </div>
                  <div className="bg-[#1E293B] rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
                    <p className="text-sm font-medium text-primary mb-1">Up Next: {currentExercise.exercise_name}</p>
                    <p className="text-sm text-text-secondary">
                      {currentNumSets} sets × {getTargetReps(currentExercise, 0)} reps
                    </p>
                  </div>
                </div>
                
                {/* Based Off exercise — show read-only max and auto-continue */}
                {currentExercise.based_off_exercise_id && currentExercise.based_off_exercise_id !== currentExercise.exercise_id ? (
                  <div className="flex gap-3 ml-11">
                    <div className="bg-surface border border-cyan-500/30 rounded-xl p-4 flex-1" data-testid="guided-based-off-banner">
                      <div className="flex items-center gap-2 mb-2">
                        <Link2 className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm text-cyan-400 font-semibold">Using {currentExercise.based_off_exercise_name} Max</span>
                      </div>
                      <p className="text-xl font-bold text-white ml-6">
                        {userMaxes[currentExercise.based_off_exercise_id] || "—"} <span className="text-xs text-cyan-400/70 font-normal">lbs</span>
                      </p>
                      <p className="text-[10px] text-cyan-400/50 ml-6 mt-1">Weights are calculated from your {currentExercise.based_off_exercise_name} max</p>
                      <button
                        onClick={() => setConversationStep("show_exercise")}
                        className="w-full mt-3 bg-primary text-white py-2 rounded-lg font-medium text-sm"
                        data-testid="continue-based-off"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                ) : (
                <div className="flex gap-3 ml-11">
                  <div className="bg-surface border border-border rounded-xl p-4 flex-1">
                    <p className="text-sm mb-3">What's your max for this lift? (optional)</p>
                    <p className="text-xs text-text-secondary mb-3">This helps me calculate your target weights based on percentages.</p>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={tempMaxInput}
                        onChange={(e) => setTempMaxInput(e.target.value)}
                        placeholder="e.g., 225"
                        className="flex-1 bg-[#1E293B] border border-[#1E293B] rounded-lg px-3 py-2 text-white text-sm"
                        data-testid="max-input"
                      />
                      <span className="flex items-center text-text-secondary text-sm">lbs</span>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={handleMaxSubmit}
                        className="flex-1 bg-primary text-white py-2 rounded-lg font-medium text-sm"
                        data-testid="submit-max"
                      >
                        {tempMaxInput ? "Set Max" : "Continue"}
                      </button>
                      {userMaxes[currentExercise.exercise_id] && (
                        <button
                          onClick={skipMaxEntry}
                          className="px-4 py-2 bg-[#2D3B4F] text-text-secondary rounded-lg text-sm"
                        >
                          Use Previous ({userMaxes[currentExercise.exercise_id]} lbs)
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                )}
              </div>
            ) : (
              <>
                {/* Exercise announcement (show at start of new exercise) */}
                {currentSetIndex === 0 && (
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-primary">
                    {String.fromCharCode(65 + currentExercise.sectionIndex)}
                  </span>
                </div>
                <div className="bg-[#1E293B] rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
                  <p className="text-sm text-text-secondary">{currentExercise.sectionName}</p>
                  <p className="font-medium">{currentExercise.exercise_name}</p>
                  <p className="text-xs text-text-muted mt-1">
                    {currentNumSets} sets • Exercise {currentExerciseIndex + 1} of {totalExercises}
                  </p>
                </div>
              </div>
            )}

            {/* Current Set Card */}
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/30 rounded-2xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-text-secondary uppercase tracking-wider">Set</p>
                  <p className="font-heading text-3xl font-bold">{currentSetIndex + 1} <span className="text-lg text-text-secondary">/ {currentNumSets}</span></p>
                </div>
                {userMax && (
                  <div className="text-right">
                    <p className="text-xs text-amber-400">Your Max</p>
                    <p className="font-bold text-amber-400">{userMax} lbs</p>
                  </div>
                )}
              </div>

              {/* Weight Info */}
              {targetWeight ? (
                <div className="bg-[#0B0F1A] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-text-secondary">Target Weight</span>
                    {percentage && <span className="text-xs text-primary">{percentage}% of max</span>}
                  </div>
                  <p className="font-heading text-4xl font-bold text-primary">{targetWeight} lbs</p>
                  
                  {matchedEquipment && plateBreakdown && (
                    <div className="mt-3 pt-3 border-t border-[#1E293B]">
                      <p className="text-xs text-text-muted mb-1">Using {matchedEquipment.name} ({matchedEquipment.weight} lbs)</p>
                      <p className="text-sm text-accent font-medium">
                        Each side: {formatPlateBreakdown(plateBreakdown)}
                      </p>
                      <p className="text-[10px] text-amber-400/70 italic mt-1">Load plates symmetrically — inner plates go on first, come off last</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-[#0B0F1A] rounded-xl p-4 text-center">
                  <p className="text-text-muted text-sm">No max set for this exercise</p>
                </div>
              )}

              {/* Reps */}
              <div className="flex items-center justify-center gap-2">
                <Target className="w-5 h-5 text-text-secondary" />
                <span className="text-lg font-semibold">{targetReps} reps</span>
              </div>

              {/* Actual Weight Input */}
              <div>
                <label className="text-xs text-text-secondary block mb-1">Actual weight used (optional)</label>
                <input
                  type="number"
                  value={actualWeight}
                  onChange={(e) => setActualWeight(e.target.value)}
                  placeholder={targetWeight ? targetWeight.toString() : "0"}
                  className="w-full bg-[#1E293B] border border-[#1E293B] rounded-lg px-4 py-3 text-white text-lg font-semibold text-center focus:border-primary outline-none"
                  data-testid="actual-weight-input"
                />
              </div>

              {/* Done Button */}
              <button
                onClick={() => setShowFeedback(true)}
                className="w-full bg-success text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-green-600 transition-colors"
                data-testid="set-done-btn"
              >
                <Check className="w-6 h-6" />
                Done
              </button>
            </div>
              </>
            )}
          </motion.div>
        ) : null}

        <div ref={chatEndRef} />
      </div>
    </motion.div>
  );
};

export { GuidedWorkoutMode };
